'use client';

import React, { useState } from 'react';

// ─── EXPLORE FURTHER TOGGLE ───────────────────────────────────────────────────
// Wraps any platform callout, audit annotation, or architecture demand box
// as an optional collapsible that sits discreetly beneath each section.
// Open by default — primary letter text flows uninterrupted when minimised.

interface ExploreFurtherProps {
  label?: string;
  children: React.ReactNode;
  accentColor?: string;
  defaultOpen?: boolean;
}

export default function ExploreFurther({
  label = 'Explore Further',
  children,
  accentColor = 'rgba(201,168,76,0.5)',
  defaultOpen = true,
}: ExploreFurtherProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ marginTop: 24 }}>
      {/* Toggle trigger — visible and accessible */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: open ? 12 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-block',
              width: 6, height: 6, borderRadius: '50%',
              background: accentColor,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 9,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: open ? accentColor : 'rgba(212,197,169,0.35)',
              transition: 'color 0.2s',
            }}
          >
            {label}
          </span>
        </div>

        {/* Minimize / expand button — clearly visible */}
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            background: open ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${open ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.06)'}`,
            borderRadius: 4,
            cursor: 'pointer',
            padding: '4px 10px',
            color: open ? 'rgba(212,197,169,0.6)' : 'rgba(212,197,169,0.3)',
            transition: 'all 0.2s',
          }}
          aria-expanded={open}
          aria-label={open ? 'Minimise this section' : 'Expand this section'}
          title={open ? 'Minimise' : 'Expand'}
        >
          <span
            style={{
              display: 'inline-block',
              fontSize: 8,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            ▲
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase' }}>
            {open ? 'Minimise' : 'Expand'}
          </span>
        </button>
      </div>

      {/* Collapsible content */}
      {open && (
        <div
          style={{
            paddingTop: 16,
            borderTop: `1px solid rgba(201,168,76,0.08)`,
            animation: 'efFadeIn 0.22s ease',
          }}
        >
          <style>{`@keyframes efFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          {children}
        </div>
      )}

      {/* Collapsed state — subtle indicator */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 0',
            color: 'rgba(212,197,169,0.2)',
            transition: 'color 0.2s',
          }}
        >
          <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase' }}>
            ▶ {label} (minimised)
          </span>
        </button>
      )}
    </div>
  );
}
